import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Building2, LogIn, LogOut, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { getApiErrorMessage } from '@/lib/apiError';
import { selfToday, selfCheckIn, selfCheckOut, formatMinutes, type SelfDay, type CaptureMode } from '@/services/attendanceService';
import { closingOnly, selfCardState, selfCardVisible } from './selfCardState';

// Office self check-in (scanner-less offices): renders ONLY when the caller's
// branch capture mode is self_checkin — offices with biometric scanners or
// untracked presence never see it. One card, one button: in when you arrive,
// out when you leave; the day flows into the normal attendance review.
export const OfficeCheckInCard: React.FC = () => {
  const { t } = useTranslation('admin');
  const [mode, setMode] = useState<CaptureMode | 'none' | null>(null);
  const [backup, setBackup] = useState(false);
  const [openSession, setOpenSession] = useState(false);
  const [day, setDay] = useState<SelfDay | null>(null);
  const [busy, setBusy] = useState(false);

  // DEF-0mvnbr7sd001 (B8 review follow-up): an answer is applied only if no
  // punch succeeded since it was ASKED and it is the latest read. A slow
  // re-read from before a newer check-out would otherwise put back "checked
  // in" and offer Check out again.
  const punchGen = useRef(0);
  const readSeq = useRef(0);

  // afterPunch: a re-read after a successful check-in/out. If THAT read fails,
  // keep what the punch itself returned — never fall back to the pre-punch state.
  const load = (afterPunch = false) => {
    const gen = punchGen.current;
    const seq = ++readSeq.current;
    const current = () => gen === punchGen.current && seq === readSeq.current;
    selfToday()
      .then((r) => {
        if (!current()) return;
        setMode(r.mode); setBackup(r.backup === true); setOpenSession(r.open_session === true); setDay(r.day);
      })
      .catch(() => { if (current() && !afterPunch) setMode('none'); });
  };
  useEffect(() => { load(); }, []);

  // Only when self check-in is actually allowed (self check-in office, or a
  // scanner office with backup turned on by an admin).
  // …or when they are still checked in from before it was turned off (B8).
  if (!selfCardVisible(mode, backup, openSession)) return null;

  // On a scanned day the scanner's last punch is not a check-out — see selfCardState.
  const state = selfCardState(day, { backup: mode === 'biometric' && backup, closingOnly: closingOnly(mode, backup, openSession) });

  // ⚠️ DEF-0mvn5ckmp005 (B8 follow-up): a check-out that SUCCEEDED followed by
  // a failed re-read left the card on its stale "checked in" state, offering
  // Check out again. The server's answer to the punch IS the new state: apply
  // it (the day it returns + whether a session is now open) before re-reading.
  const act = async (kind: 'check_in' | 'check_out') => {
    setBusy(true);
    try {
      const r = kind === 'check_out' ? await selfCheckOut() : await selfCheckIn();
      toast.success(kind === 'check_out'
        ? t('att.self_out_ok', { defaultValue: 'Checked out — have a good one!' })
        : t('att.self_in_ok', { defaultValue: 'Checked in' }));
      punchGen.current += 1; // every read asked before this punch is now stale
      if (r?.day) setDay(r.day);
      setOpenSession(kind === 'check_in');
      load(true);
    }
    catch (e) { toast.error(getApiErrorMessage(e, t('att.self_err', { defaultValue: 'Could not record your check-in.' }))); }
    finally { setBusy(false); }
  };

  return (
    <Card className="border-orange-200 dark:border-orange-500/30">
      <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4">
        <div className="flex items-center gap-3 min-w-0">
          <Building2 className="w-6 h-6 text-orange-500 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium text-foreground">{t('att.self_title', { defaultValue: 'Office check-in' })}</p>
            <p className="text-xs text-muted-foreground">
              {t(state.key, {
                defaultValue: state.defaultValue,
                ...(state.values || {}),
                ...(state.net !== undefined ? { net: formatMinutes(state.net) } : {}),
              })}
            </p>
          </div>
        </div>
        {state.action !== 'none' && (
          state.action === 'check_out' ? (
            <Button variant="outline" disabled={busy} onClick={() => act('check_out')}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LogOut className="w-4 h-4 mr-1" />}
              {t('att.self_out', { defaultValue: 'Check out' })}
            </Button>
          ) : (
            <Button disabled={busy} onClick={() => act('check_in')}>
              {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <LogIn className="w-4 h-4 mr-1" />}
              {t('att.self_in', { defaultValue: 'Check in' })}
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default OfficeCheckInCard;
